import {
  NoteCodeSystem,
  NoteContextKey,
  NoteTeethGroup,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import { templateSpecialtyMessages } from './i18n';
import type {
  CatalogSource,
  DocumentType,
  VersionStatus,
  SectionProcessingType,
  DocumentInclusion,
} from './types';

export const VERSION_STATUSES: VersionStatus[] = [
  'draft',
  'active',
  'archived',
  'deleted',
];

export const DOCUMENT_TYPES: DocumentType[] = [
  'SUMMARY',
  'EVALUATION',
  'PROTOCOL',
  'LITERATURE_REVIEW',
  'FOLLOW_UP_LETTER',
];

// Transcriptor v2 keeps a letter apart from a follow-up letter. Only content
// stamped for it reads a stored `letter` as LETTER.
export const TRANSCRIPTOR_V2_DOCUMENT_TYPES: DocumentType[] = [
  'SUMMARY',
  'EVALUATION',
  'PROTOCOL',
  'LITERATURE_REVIEW',
  'LETTER',
  'FOLLOW_UP_LETTER',
];

export const WIRE_DOCUMENT_TYPE_ALIASES: Record<string, DocumentType> = {
  summary: 'SUMMARY',
  evaluation: 'EVALUATION',
  protocol: 'PROTOCOL',
  letter: 'FOLLOW_UP_LETTER',
  literature_review: 'LITERATURE_REVIEW',
};

export const DEFAULT_DOCUMENT_TYPE: DocumentType = 'PROTOCOL';

export const SECTION_PROCESSING_TYPES: SectionProcessingType[] = [
  'STANDARD',
  'EXTENDED',
];

export const DOCUMENT_INCLUSIONS: DocumentInclusion[] = ['always', 'on_demand'];

export const TEMPLATE_SPECIALTIES = Object.keys(templateSpecialtyMessages);

export const MAX_ALWAYS_DOCUMENTS = 3;

// The Templates page opens the template series named here (?series=<id>).
export const TEMPLATE_SERIES_SEARCH_PARAM = 'series';

// Opens the named series in the edit mode.
export const TEMPLATE_EDIT_SEARCH_PARAM = 'edit';

// A box text is typed into, which keeps the undo and redo keys for its own
// text; a checkbox, a radio or a button is not one.
export const TEXT_BOX_SELECTOR =
  'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), [contenteditable]:not([contenteditable="false"])';

export const CONTRACT_BUCKET_RE = /(P|AI)\s*\[([^\]]*)\]/gi;
export const PLACEHOLDER_BRACES_RE = /\{\{\s*([^}]+)\s*\}\}/g;
export const PLACEHOLDER_ANGLE_RE = /<\s*([^>]+)\s*>/g;
export const BRACKETED_CODE_RE = /\[{2,}\s*(?:[^[\]]*?)\s*\]{2,}/g;
export const MULTI_UNDERSCORE_RE = /_{2,}/g;
export const SINGLE_UNDERSCORE_RE = /(^|[\s(:;,])_(?=$|[\s),;.:])/g;
export const STRIP_INDEX_RE = /^\d+(?:[.)]|\.\d+)?\s*/;
export const SECTION_COUNT_SUFFIX_RE = /\s*\((\d+)\)\s*$/;
export const TERM_SPLIT_RE = /[|;,]/;
export const COUNT_ONLY_RE = /^\((\d+)\):?$/;
export const MALFORMED_COUNT_PREFIX_RE = /^\((\d+)\):\s*(.*)$/;
export const AI_HINT_RE = /(^|\s)(ai|ии)(\s|$)/i;
export const GENERIC_SLOT_NAME_RE =
  /^(?:__ai_)?(?:field|slot|value|param|var|variable)_?\d*$/i;
export const CYRILLIC_RE = /[А-Яа-яЁё]/;
export const SURFACE_VALUE_HINT_RE =
  /(окклюз|медиал|дистал|вестиб|небн|язычн|буккал|labial|lingual|palat|occlus|mesial|distal|buccal|facial)/i;
export const BLACK_CLASS_VALUE_RE =
  /^(?:\d+\s*класс|class\s*[ivx]+|\d+\s*class)$/i;
export const IROPZ_VALUE_HINT_RE = /(ироп|irop)/i;

export const RUSSIAN_FILLER_WORDS = new Set([
  'и',
  'или',
  'в',
  'во',
  'на',
  'по',
  'с',
  'со',
  'к',
  'ко',
  'для',
  'от',
  'под',
  'при',
  'после',
  'до',
  'из',
  'без',
  'над',
  'за',
  'перед',
  'проведена',
  'проведено',
  'проведен',
  'провести',
  'нанесение',
  'нанесена',
  'нанесен',
  'обработана',
  'обработан',
  'очищена',
  'очищен',
  'внесение',
  'внесен',
  'подготовка',
  'подготовлена',
  'подготовлен',
]);

export const GENERIC_FIELD_LABELS: Record<string, string> = {
  en: 'Description',
  ru: 'Описание',
  de: 'Beschreibung',
  es: 'Descripción',
  pt: 'Descrição',
};

// How many widgets of a note render again at once when a new note comes: a
// few in the task that brought it, which also builds and patches the whole
// note, then a slice per frame, so a note filled at once never renders every
// widget in one long task.
export const NOTE_BLOCK_NOTIFY_FIRST_SLICE = 20;

export const NOTE_BLOCK_NOTIFY_SLICE = 80;

// Text typed into a note's document is committed after this pause, unless
// leaving the block or the document commits it first.
export const NOTE_TEXT_COMMIT_IDLE_MS = 1000;

// The values from the record that hold a date; the others hold text.
export const NOTE_DATE_CONTEXT_KEYS: NoteContextKey[] = [
  NoteContextKey.NoteContextKeyVisitDate,
  NoteContextKey.NoteContextKeyPatientDateOfBirth,
];

// Every group a teeth field can offer, in the protocol's order: both
// arches, the four quadrants and both sides.
export const NOTE_TEETH_GROUPS: NoteTeethGroup[] = [
  NoteTeethGroup.NoteTeethGroupUpper,
  NoteTeethGroup.NoteTeethGroupLower,
  NoteTeethGroup.NoteTeethGroupUpperRight,
  NoteTeethGroup.NoteTeethGroupUpperLeft,
  NoteTeethGroup.NoteTeethGroupLowerLeft,
  NoteTeethGroup.NoteTeethGroupLowerRight,
  NoteTeethGroup.NoteTeethGroupRight,
  NoteTeethGroup.NoteTeethGroupLeft,
];

// The code systems a diagnosis field may name, in the order they are
// offered, with the code format scribe checks (`note_code_system.go`: codes
// as written, no space or lowercase) and the example its messages give.
export const NOTE_CODE_SYSTEMS: Record<
  number,
  { name: string; example: string; format: RegExp }
> = {
  [NoteCodeSystem.NoteCodeSystemICD10]: {
    name: 'ICD-10',
    example: 'K07.20',
    format: /^[A-Z][0-9]{2}(?:\.[0-9]{1,2})?$/,
  },
  [NoteCodeSystem.NoteCodeSystemICD10CM]: {
    name: 'ICD-10-CM',
    example: 'M26.211',
    format: /^[A-Z][0-9][0-9A-Z](?:\.[0-9A-Z]{1,4})?$/,
  },
  [NoteCodeSystem.NoteCodeSystemICD11]: {
    name: 'ICD-11',
    example: 'DA0A.0',
    format:
      /^[1-9A-HJ-NP-Z][A-HJ-NP-Z][0-9][0-9A-HJ-NP-Z](?:\.[0-9A-HJ-NP-Z]{1,2})?$/,
  },
};

// The neutral code of an option or a tooth code: lowercase Latin letters,
// digits and underscores, starting with a letter, at most 64.
export const NOTE_OPTION_CODE_FORMAT = /^[a-z][a-z0-9_]{0,63}$/;

export const CATALOG_SOURCES: CatalogSource[] = ['saved', 'ours', 'partners'];

// The department of the templates no known department claims.
export const CATALOG_OTHER_DEPARTMENT = 'other';
